import { useCallback, useContext, useEffect, useRef } from 'react';
import { RemotionPlayerContext } from '../context/RemotionPlayerContext';
import { FPS_24 } from '@/App/remotion/constants';

export const useRemotionPlayerSeek = () => {
  const { playerRef } = useContext(RemotionPlayerContext);

  const seekListenersRef = useRef<Set<(time: number) => void>>(new Set());

  const subscribeToSeekEvents = useCallback((listener: (time: number) => void) => {
    seekListenersRef.current.add(listener);

    return () => {
      seekListenersRef.current.delete(listener);
    };
  }, []);

  const notifySeekListeners = useCallback((time: number) => {
    seekListenersRef.current.forEach(listener => listener(time));
  }, []);

  useEffect(() => {
    if (playerRef) {
      const handleSeek = ({ detail: { frame } }: { detail: { frame: number } }) => {
        notifySeekListeners(frame / FPS_24);
      };

      playerRef.addEventListener('seeked', handleSeek);
      return () => {
        playerRef.removeEventListener('seeked', handleSeek);
      };
    }
  }, [notifySeekListeners, playerRef]);

  const seekTo = useCallback(
    (duration: number) => {
      if (!playerRef) {
        return null;
      }
      const seekToFrame = Math.round(duration * FPS_24);
      playerRef.seekTo(seekToFrame);
    },
    [playerRef]
  );

  return { subscribeToSeekEvents, seekTo };
};
